import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { restartableTask, dropTask } from 'ember-concurrency';
import { isString } from 'lodash';

import { SOCIAL_PROFILE_CODES } from 'later/utils/constants';

import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type UserAgentService from 'ember-useragent/services/user-agent';
import type GroupModel from 'later/models/group';
import type SocialIdentityModel from 'later/models/social-identity';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type CacheService from 'later/services/cache';
import type DialogManagerService from 'later/services/dialog-manager';
import type ErrorsService from 'later/services/errors';
import type InstagramService from 'later/services/instagram';
import type LinkedinService from 'later/services/linkedin';
import type PinterestService from 'later/services/pinterest';
import type SegmentService from 'later/services/segment';
import type SnapchatService from 'later/services/snapchat';
import type SocialAuthEventsService from 'later/services/social/auth-events';
import type { SocialAuthEventFlowId, SocialAuthEventProvider } from 'later/services/social/auth-events';
import type FacebookAuthService from 'later/services/social/facebook-auth';
import type InstagramAuthService from 'later/services/social/instagram-auth';
import type ThreadsService from 'later/services/threads';
import type YoutubeService from 'later/services/youtube';
import type { OauthSocialProfileType } from 'later/utils/constants';
import type { UntypedService, Maybe } from 'shared/types';

interface AddSocialProfileArgs {
  selectedSocialProfileType: string;
  group: GroupModel;
  area?: string;
  redirectPath?: Maybe<string>;
  passedInSocialIdentityId?: string | null;
  shouldRedirect?: boolean;
  shouldRedirectToSettings?: boolean;
  useLegacyInstagramLogin?: boolean;
}

interface AddLinkedinProfileArgs {
  id: string;
  group: GroupModel;
  path: string;
}

interface PendingInstagramConnection {
  group: GroupModel;
  redirectPath?: Maybe<string>;
  socialIdentityId?: Maybe<string>;
}

interface InstagramConnectionDeferred {
  resolve: () => void;
  reject: (reason: unknown) => void;
}

enum TikTokStrategy {
  TransitionToPlans = 'transitionToPlans',
  TransitionToSettings = 'transitionToSettings',
  ConnectTikTokOauth = 'connectTikTokOauth'
}

type WritableProperties<T> = { -readonly [P in keyof T]: T[P] }; // this type doesn't prevent setting getters

type ServiceKeyType<T> = keyof T;

type ServicePropertyType<T> = T[keyof T];

type KeyOfSocialProfileCodes = keyof typeof SOCIAL_PROFILE_CODES;

export default class ConnectProfilesService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare cache: CacheService;
  @service declare dialogManager: DialogManagerService;
  @service('social/instagram-auth') declare instagramAuth: InstagramAuthService;
  @service declare instagram: InstagramService;
  @service declare intl: IntlService;
  @service declare pinterest: PinterestService;
  @service declare linkedin: LinkedinService;
  @service declare tiktok: UntypedService;
  @service declare errors: ErrorsService;
  @service declare store: Store;
  @service declare router: RouterService;
  @service('social/facebook-auth') declare facebookAuth: FacebookAuthService;
  @service declare segment: SegmentService;
  @service('social/auth-events') declare authEvents: SocialAuthEventsService;
  @service declare threads: ThreadsService;
  @service declare userAgent: UserAgentService;
  @service declare youtube: YoutubeService;
  @service declare snapchat: SnapchatService;

  @tracked selectedSocialIdentity: SocialIdentityModel | undefined;
  @tracked isInstagramConnectionModalVisible = false;

  #pendingInstagramConnection: PendingInstagramConnection | undefined = undefined;
  #instagramConnectionDeferred: InstagramConnectionDeferred | undefined = undefined;

  socialProfileNames = Object.keys(SOCIAL_PROFILE_CODES).reduce<Record<string, string>>(
    (keyValueSwapResult: Record<string, string>, key) => {
      keyValueSwapResult[SOCIAL_PROFILE_CODES[key as KeyOfSocialProfileCodes]] = key.toLowerCase();
      return keyValueSwapResult;
    },
    {}
  );

  get socialIdentityId(): string | undefined {
    return this.socialIdentity?.get('id');
  }

  get socialIdentity(): SocialIdentityModel | undefined {
    if (isPresent(this.selectedSocialIdentity)) {
      return this.selectedSocialIdentity;
    }
    return this.auth.currentGroup.socialIdentities.get('length') > 0
      ? this.auth.currentGroup.socialIdentities.firstObject
      : undefined;
  }

  get hasSocialProfile(): boolean {
    return isPresent(this.auth.currentGroup?.socialProfiles);
  }

  oAuthPath({
    socialProfileType,
    redirectGroupSlug,
    flowId,
    // Note: Using an 'id' instead of a 'slug' here will trigger a redirect
    redirectPath = `${redirectGroupSlug || this.auth.currentGroup?.slug}/schedule/calendar`
  }: {
    socialProfileType: OauthSocialProfileType;
    redirectGroupSlug?: string;
    redirectPath?: Maybe<string>;
    flowId?: SocialAuthEventFlowId;
  }): string {
    const path = `/oauth/${socialProfileType}?redirect_path=${redirectPath}`;
    return flowId ? `${path}&flow_id=${flowId}` : path;
  }

  @action
  updateProperty(propertyName: ServiceKeyType<this>, value: ServicePropertyType<this>): void {
    (this as WritableProperties<typeof this>)[propertyName] = value;
  }

  addSocialProfile = restartableTask(
    async ({
      selectedSocialProfileType,
      group,
      redirectPath,
      area = '',
      shouldRedirectToSettings = false,
      passedInSocialIdentityId = this.socialIdentityId, // If null, A new social identity will be created
      shouldRedirect = true,
      useLegacyInstagramLogin = false
    }: AddSocialProfileArgs) => {
      try {
        if (isPresent(area)) {
          this.segment.track(`attempted-connecting-${this.socialProfileNames[selectedSocialProfileType]}-account`, {
            area
          });
        }

        const isTikTokRedirect =
          selectedSocialProfileType === SOCIAL_PROFILE_CODES.TIKTOK &&
          this.#decideStrategyForTikTok(shouldRedirectToSettings) !== TikTokStrategy.ConnectTikTokOauth;
        const isInstagramFacebookSdkFlow =
          selectedSocialProfileType === SOCIAL_PROFILE_CODES.INSTAGRAM &&
          useLegacyInstagramLogin &&
          Boolean(this.auth.currentAccount?.rolloutIgWithFbLoginOauth);
        const provider =
          selectedSocialProfileType === SOCIAL_PROFILE_CODES.TWITTER || isTikTokRedirect
            ? undefined
            : (this.socialProfileNames[selectedSocialProfileType] as SocialAuthEventProvider | undefined);
        let flowId: SocialAuthEventFlowId | undefined;

        if (provider) {
          flowId = this.authEvents.mintFlowId();
          const isPopup =
            selectedSocialProfileType === SOCIAL_PROFILE_CODES.FACEBOOK ||
            isInstagramFacebookSdkFlow ||
            (selectedSocialProfileType === SOCIAL_PROFILE_CODES.INSTAGRAM && this.userAgent.device.isMobile);

          void this.authEvents.emit({
            provider,
            flow: 'connect',
            stage: 'started',
            flowId,
            groupId: group.id,
            isPopup
          });
        }

        switch (selectedSocialProfileType) {
          case SOCIAL_PROFILE_CODES.INSTAGRAM:
            if (this.#shouldOfferInstagramConnectionChoice(passedInSocialIdentityId)) {
              // Let the user choose between Instagram Login and the Facebook API instead of forcing a
              // single path. Awaiting preserves `addSocialProfile`'s "resolves when done" contract: it
              // settles when the chosen flow finishes or the user dismisses the modal.
              await this.openInstagramConnectionModal({
                group,
                redirectPath,
                socialIdentityId: passedInSocialIdentityId
              });
            } else if (!useLegacyInstagramLogin) {
              await this.instagram.createInstagramWithSet(passedInSocialIdentityId, group, redirectPath, flowId);
            } else {
              await this._connectInstagram.perform(
                group,
                shouldRedirectToSettings,
                redirectPath,
                passedInSocialIdentityId,
                flowId
              );
            }
            break;
          case SOCIAL_PROFILE_CODES.TWITTER:
            this.alerts.alert(this.intl.t('alerts.twitter_deprecation.message'), {
              title: this.intl.t('alerts.twitter_deprecation.title')
            });
            break;
          case SOCIAL_PROFILE_CODES.PINTEREST:
            await this.pinterest.createPinterestWithSet(passedInSocialIdentityId, group, redirectPath, flowId);
            break;
          case SOCIAL_PROFILE_CODES.FACEBOOK:
            await this._connectFacebook.perform(group.id, shouldRedirect, passedInSocialIdentityId, flowId);
            break;
          case SOCIAL_PROFILE_CODES.LINKEDIN:
            await this.linkedin.login(group, redirectPath, flowId);
            break;
          case SOCIAL_PROFILE_CODES.TIKTOK:
            await this.connectTikTok.perform(group, shouldRedirectToSettings, redirectPath, flowId);
            break;
          case SOCIAL_PROFILE_CODES.YOUTUBE:
            await this.youtube.createYoutubeWithSet(passedInSocialIdentityId, group, redirectPath, flowId);
            break;
          case SOCIAL_PROFILE_CODES.THREADS:
            this.threads.createThreadsWithSet(passedInSocialIdentityId, group, redirectPath, flowId);
            break;
          case SOCIAL_PROFILE_CODES.SNAPCHAT:
            this.snapchat.createSnapchatWithSet(this.socialIdentityId, group, redirectPath, flowId);
            break;
          default:
            break;
        }
      } catch (error) {
        this.errors.log(error);
        // Display contextual error message to user
        this.alerts.alert(this.intl.t('alerts.account.controllers.groups.reconnect_error'), {
          title: this.intl.t('alerts.account.controllers.groups.facebook.refresh_error_title')
        });
      }
    }
  );

  // The choice modal is gated per social identity via `canSkipInstagramLogin`. With no identity yet
  // (e.g. first connect during onboarding) or the record not in the store, the flag can't be read, so
  // we fall back to the standard Instagram Login flow rather than guessing.
  #shouldOfferInstagramConnectionChoice(socialIdentityId: Maybe<string>): boolean {
    const socialIdentity = socialIdentityId
      ? this.store.peekRecord('social-identity', socialIdentityId)
      : this.socialIdentity;
    return Boolean(socialIdentity?.canSkipInstagramLogin);
  }

  // Returns a promise that settles only when the connection flow finishes: it resolves on success or
  // dismissal, and REJECTS on failure so `addSocialProfile`'s outer catch logs/alerts like every other
  // profile type. Callers awaiting `addSocialProfile` therefore never resolve on mere modal-open.
  openInstagramConnectionModal(pending: PendingInstagramConnection): Promise<void> {
    // Settle any prior pending choice first so re-opening can't strand an earlier awaiter.
    this.#instagramConnectionDeferred?.resolve();
    this.#pendingInstagramConnection = pending;
    this.isInstagramConnectionModalVisible = true;
    return new Promise<void>((resolve, reject) => {
      this.#instagramConnectionDeferred = { resolve, reject };
    });
  }

  @action
  closeInstagramConnectionModal(): void {
    this.isInstagramConnectionModalVisible = false;
    this.#pendingInstagramConnection = undefined;
    this.#instagramConnectionDeferred?.resolve();
    this.#instagramConnectionDeferred = undefined;
  }

  #failInstagramConnection(error: unknown): void {
    this.isInstagramConnectionModalVisible = false;
    this.#pendingInstagramConnection = undefined;
    this.#instagramConnectionDeferred?.reject(error);
    this.#instagramConnectionDeferred = undefined;
  }

  // Instagram Login OAuth redirect flow (BE-driven), then optional Facebook "supercharge".
  connectViaInstagramLogin = dropTask(async () => {
    const pending = this.#pendingInstagramConnection;
    if (!pending) {
      this.closeInstagramConnectionModal();
      return;
    }
    try {
      await this.instagram.createInstagramWithSet(pending.socialIdentityId, pending.group, pending.redirectPath);
      this.closeInstagramConnectionModal();
    } catch (error) {
      this.#failInstagramConnection(error);
    }
  });

  // Facebook Graph API via the SDK, then the page selector modal creates the profile. Intentionally not
  // gated on `rolloutIgWithFbLoginOauth` (the modal itself is gated by `canSkipInstagramLogin`); revisit
  // if that kill switch must also cover this entry point once the flag ships.
  connectViaFacebookApi = dropTask(async () => {
    const pending = this.#pendingInstagramConnection;
    if (!pending) {
      this.closeInstagramConnectionModal();
      return;
    }
    try {
      await this.instagramAuth.authenticateWithFacebookGraphApi.perform({
        socialIdentityId: pending.socialIdentityId || undefined
      });
      this.closeInstagramConnectionModal();
    } catch (error) {
      this.#failInstagramConnection(error);
    }
  });

  addLinkedinProfile = dropTask(async ({ id, group, path }: AddLinkedinProfileArgs) => {
    try {
      const socialIdentityId = this.cache.retrieve('linkedin_selected_social_identity_id');

      let socialIdentity: Maybe<SocialIdentityModel>;
      if (socialIdentityId && isString(socialIdentityId) && socialIdentityId !== '0') {
        socialIdentity = await this.store.findRecord('social-identity', socialIdentityId);
      } else {
        socialIdentity = undefined;
      }

      await this.linkedin.addProfile(id, group, socialIdentity);
      this.cache.remove('linkedin_selected_social_identity_id');
      this.router.transitionTo(path, group, { queryParams: { show_linkedin_selector: false } });
    } catch (error) {
      this.errors.log(error);
    }
  });

  _connectInstagram = dropTask(
    async (
      group: GroupModel,
      shouldRedirectToSettings: boolean,
      redirectPath?: string | null,
      passedInSocialIdentityId?: string | null,
      flowId?: SocialAuthEventFlowId
    ) => {
      const didConfirm = await this.dialogManager.instagramConnect({
        message: this.intl.t('shared_phrases.add_instagram'),
        description: this.intl.t('credentials.ig_facebook_connect_modal.connect_info'),
        learnMoreLink:
          'https://help.later.com/hc/en-us/articles/1500001601602-Why-Does-Later-Ask-Me-to-Connect-Instagram-to-a-Facebook-Page'
      });
      if (!didConfirm) return;

      if (this.auth.currentAccount?.rolloutIgWithFbLoginOauth) {
        await this.instagramAuth.authenticateWithFacebookGraphApi.perform({
          socialIdentityId: passedInSocialIdentityId || undefined,
          socialAuthFlowId: flowId
        });
      } else {
        await this.connectInstagramPersonal.perform(
          group,
          shouldRedirectToSettings,
          redirectPath,
          passedInSocialIdentityId,
          flowId
        );
      }
    }
  );

  _connectFacebook = dropTask(
    async (
      groupId: string,
      shouldRedirect: boolean,
      passedInSocialIdentityId?: string | null,
      flowId?: SocialAuthEventFlowId
    ) => {
      const group = this.store.peekRecord('group', groupId);

      if (!group) {
        throw new Error(`Unable to find Group record with provided ID: ${groupId}`);
      }

      if (shouldRedirect) {
        this.router.transitionTo('cluster.schedule.calendar', group.slug);
      }

      await this.facebookAuth.authenticate.perform({
        socialIdentityId: passedInSocialIdentityId || undefined,
        socialAuthFlowId: flowId
      });
    }
  );

  connectInstagramPersonal = dropTask(
    async (
      group: GroupModel,
      shouldRedirectToSettings: boolean,
      redirectPath?: string | null,
      passedInSocialIdentityId?: string | null,
      flowId?: SocialAuthEventFlowId
    ) => {
      if (shouldRedirectToSettings) {
        this.router.transitionTo('account.groups.group.social_profiles.new', group.id);
      } else {
        await this.instagram.createInstagramWithSet(passedInSocialIdentityId, group, redirectPath, flowId);
      }
    }
  );

  connectTikTok = dropTask(
    async (
      group: GroupModel,
      shouldRedirectToSettings: boolean,
      redirectPath?: string | null,
      flowId?: SocialAuthEventFlowId
    ) => {
      const tikTokStrategy = this.#decideStrategyForTikTok(shouldRedirectToSettings);

      switch (tikTokStrategy) {
        case TikTokStrategy.TransitionToSettings:
          this.router.transitionTo('account.groups.group.social_profiles.new', group.id);
          break;
        case TikTokStrategy.TransitionToPlans:
          this.router.transitionTo('plans.migrate');
          break;
        case TikTokStrategy.ConnectTikTokOauth:
          await this.tiktok.createTiktokWithSet(this.socialIdentityId, group, redirectPath, flowId);
          break;
        default:
          break;
      }
    }
  );

  #decideStrategyForTikTok(shouldRedirectToSettings: boolean): TikTokStrategy {
    const { isOnLegacyPlan } = this.auth.currentAccount || {};

    if (isOnLegacyPlan) {
      return TikTokStrategy.TransitionToPlans;
    }

    if (shouldRedirectToSettings) {
      return TikTokStrategy.TransitionToSettings;
    }

    return TikTokStrategy.ConnectTikTokOauth;
  }
}

declare module '@ember/service' {
  interface Registry {
    'social/connect-profiles': ConnectProfilesService;
  }
}
